import React, { useState, useEffect } from 'react'
import Chart from 'react-apexcharts'
import { Pagination, Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import BrandedLoading from '../../common/BrandedLoading'

const DashboardCharts = ({ chartType, counts, selectedLeadStatus = null, appointments = [], storageUsage = null }) => {
  const [chartData, setChartData] = useState(null)
  const [timeFrame, setTimeFrame] = useState('monthly')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAllStatuses, setShowAllStatuses] = useState(false)
  const dataPerPage = 15

  useEffect(() => {
    if (counts) {
      setChartData(processData(counts, appointments))
    }
    setCurrentPage(1)
    setShowAllStatuses(false)
  }, [counts, timeFrame, chartType, selectedLeadStatus, appointments])

  const processData = (data, appointmentList) => {
    const processed = {
      payments: { monthly: {}, weekly: {} },
      orders: { monthly: {}, weekly: {} },
      users: { monthly: {}, weekly: {} },
      sales: { monthly: {}, weekly: {} },
      tableBookings: { monthly: {}, weekly: {} },
      qrOrders: { monthly: {}, weekly: {} },
      crmData: { monthly: {}, weekly: {} },
      appointments: { monthly: {}, weekly: {} },
      appointmentPayments: { monthly: {}, weekly: {} }, // Add appointmentPayments structure
      emailUsage: { monthly: {} },
      smsUsage: { monthly: {} },
    }

    if (data && data.payment) {
      data.payment.forEach(payment => {
        const date = new Date(payment.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`
        const type = (payment.type === 'Square' || payment.type === 'Stripe') ? 'Online' : 'Store';

        if (!processed.payments.monthly[month]) processed.payments.monthly[month] = { Online: 0, Store: 0 };
        if (!processed.payments.weekly[week]) processed.payments.weekly[week] = { Online: 0, Store: 0 };
        processed.payments.monthly[month][type]++
        processed.payments.weekly[week][type]++
      })
    }

    if (data && data.order) {
      data.order.forEach(order => {
        const date = new Date(order.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`
        let channel;
        if (order.orderFrom === 'web') channel = 'web';
        else if (order.orderFrom === 'mobile') channel = 'mobile';
        else if (order.orderFrom === 'instore') channel = 'instore';

        if (!processed.orders.monthly[month]) processed.orders.monthly[month] = { web: 0, mobile: 0, instore: 0 }
        if (!processed.orders.weekly[week]) processed.orders.weekly[week] = { web: 0, mobile: 0, instore: 0 }
        processed.orders.monthly[month][channel]++
        processed.orders.weekly[week][channel]++
      })
    }


    if (data && data.order) {
      data.order.forEach(order => {
        if (!order.isCancelled) {
          const date = new Date(order.createdAt);
          const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`;

          const amount = parseFloat(order?.grandTotal || 0);

          if (!processed.sales.monthly[month]) processed.sales.monthly[month] = 0;
          if (!processed.sales.weekly[week]) processed.sales.weekly[week] = 0;

          processed.sales.monthly[month] += amount;
          processed.sales.weekly[week] += amount;
        }
      });
    }

    if (data && data.customer) {
      data.customer.forEach(user => {
        const date = new Date(user.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`
        let channel;
        if (user.channel === 'web') channel = 'web';
        else if (user.channel === 'mobile') channel = 'mobile';

        if (!processed.users.monthly[month]) processed.users.monthly[month] = { web: 0, mobile: 0 }
        if (!processed.users.weekly[week]) processed.users.weekly[week] = { web: 0, mobile: 0 }
        processed.users.monthly[month][channel]++
        processed.users.weekly[week][channel]++
      })
    }

    if (data && data.tableBooking) {
      data.tableBooking.forEach(booking => {
        const date = new Date(booking.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`

        if (!processed.tableBookings.monthly[month]) processed.tableBookings.monthly[month] = { bookings: 0, persons: 0 }
        if (!processed.tableBookings.weekly[week]) processed.tableBookings.weekly[week] = { bookings: 0, persons: 0 }
        processed.tableBookings.monthly[month].bookings++
        processed.tableBookings.monthly[month].persons += booking.persons || 0
        processed.tableBookings.weekly[week].bookings++
        processed.tableBookings.weekly[week].persons += booking.persons || 0
      })
    }

    if (data && data.qrOrders) {
      data.qrOrders.forEach(order => {
        const date = new Date(order.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`
        const amount = parseFloat(order?.grandTotal || 0)

        if (!processed.qrOrders.monthly[month]) processed.qrOrders.monthly[month] = { orders: 0, sales: 0 }
        if (!processed.qrOrders.weekly[week]) processed.qrOrders.weekly[week] = { orders: 0, sales: 0 }

        processed.qrOrders.monthly[month].orders++
        processed.qrOrders.monthly[month].sales += amount
        processed.qrOrders.weekly[week].orders++
        processed.qrOrders.weekly[week].sales += amount
      })
    }

    // Process CRM data
    if (data && data.crmData) {
      data.crmData.forEach(record => {
        const date = new Date(record.createdAt)
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' })
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`
        const status = record.leadStatus

        if (!processed.crmData.monthly[month]) {
          processed.crmData.monthly[month] = { Contact: 0, Lead: 0, Prospect: 0, Customer: 0 }
        }
        if (!processed.crmData.weekly[week]) {
          processed.crmData.weekly[week] = { Contact: 0, Lead: 0, Prospect: 0, Customer: 0 }
        }

        if (status) {
          processed.crmData.monthly[month][status] = (processed.crmData.monthly[month][status] || 0) + 1
          processed.crmData.weekly[week][status] = (processed.crmData.weekly[week][status] || 0) + 1
        }
      })
    }

    // Process Appointment Data
    if (appointmentList && appointmentList.length > 0) {
      appointmentList.forEach(apt => {
        // Only count valid appointments
        if (apt.isDeleted || apt.appointmentStatus === 'cancelled') return;

        // Strict filtering for "Bookings" count (Paid+Completed OR Free+Scheduled)
        const isPaidSuccess = apt.paymentStatus === 'completed' && apt.appointmentStatus === 'scheduled';
        const isPaidMode = (apt.paymentMode || '').toLowerCase() === 'paid';
        const isFreeSuccess = !isPaidMode && apt.appointmentStatus === 'scheduled';
        const isCountable = isPaidSuccess || isFreeSuccess;

        const date = new Date(apt.created); // uses 'created' field from backend
        const month = date.toLocaleString('default', { month: 'short', year: 'numeric' });
        const week = `W${getWeekNumber(date)} - ${date.getFullYear()}`;

        if (!processed.appointments.monthly[month]) processed.appointments.monthly[month] = 0;
        if (!processed.appointments.weekly[week]) processed.appointments.weekly[week] = 0;

        if (isCountable) {
          processed.appointments.monthly[month]++;
          processed.appointments.weekly[week]++;
        }

        // Process Appointment Payments

        if (isPaidMode) {
          const isSuccess = apt.paymentStatus === 'completed';
          if (!processed.appointmentPayments.monthly[month]) processed.appointmentPayments.monthly[month] = { Successful: 0, Unsuccessful: 0 };
          if (!processed.appointmentPayments.weekly[week]) processed.appointmentPayments.weekly[week] = { Successful: 0, Unsuccessful: 0 };

          if (isSuccess) {
            processed.appointmentPayments.monthly[month].Successful++;
            processed.appointmentPayments.weekly[week].Successful++;
          } else {
            processed.appointmentPayments.monthly[month].Unsuccessful++;
            processed.appointmentPayments.weekly[week].Unsuccessful++;
          }
        }
      });
    }

    // Process Email Usage Data
    if (data && data.emailUsage && data.emailUsage.history) {
      data.emailUsage.history.forEach(u => {
        const date = new Date(u.month + "-01");
        const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });
        processed.emailUsage.monthly[monthLabel] = u.count;
      });
    }

    // Process SMS Usage Data
    if (data && data.smsUsage && data.smsUsage.history) {
      data.smsUsage.history.forEach(u => {
        const date = new Date(u.month + "-01");
        const monthLabel = date.toLocaleString('default', { month: 'short', year: 'numeric' });
        processed.smsUsage.monthly[monthLabel] = u.count;
      });
    }

    return processed
  }

  const getWeekNumber = (date) => {
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7))
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
  }

  const handleTimeFrameChange = (event, newTimeFrame) => {
    if (newTimeFrame !== null) {
      setTimeFrame(newTimeFrame)
      setCurrentPage(1)
    }
  }

  if (!chartData) {
    return <BrandedLoading />
  }
  const sortCategories = (categories) => {
    return categories.sort((a, b) => {
      if (timeFrame === 'weekly') {
        const [weekA, yearA] = a.split(' - ')
        const [weekB, yearB] = b.split(' - ')
        const yearDiff = parseInt(yearB) - parseInt(yearA)
        return yearDiff !== 0 ? yearDiff : parseInt(weekB.slice(1)) - parseInt(weekA.slice(1))
      } else {
        const [monthA, yearA] = a.split(' ')
        const [monthB, yearB] = b.split(' ')
        const dateA = new Date(`${monthA} 1, ${yearA}`)
        const dateB = new Date(`${monthB} 1, ${yearB}`)

        return dateB - dateA
      }
    })
  }

  const pagination = (data) => {
    if (!data) return { paginatedCategories: [], allCategories: [] }
    const sortedCategories = sortCategories(Object.keys(data))
    const startIndex = (currentPage - 1) * dataPerPage
    const endIndex = startIndex + dataPerPage
    return {
      paginatedCategories: sortedCategories.slice(startIndex, endIndex),
      allCategories: sortedCategories
    }
  }

  const getMaxValue = (data) => {
    return Math.max(...Object.values(data).map(d => typeof d === 'object' ? Math.max(...Object.values(d)) : d))
  }

  const getCommonOptions = (title, xaxisCategories, yaxisTitle, maxVal, fullDataForExport = null) => ({
    chart: {
      fontFamily: "'Inter', sans-serif",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        },
        export: {
          csv: {
            filename: `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`,
            headerCategory: 'Period',
            dateFormatter(timestamp) {
              return new Date(timestamp).toDateString()
            },
            // Override to export full data instead of paginated data
            ...(fullDataForExport && {
              categoryFormatter: (value) => value,
              columnDelimiter: ',',
              headerCategory: 'Period',
              headerValue: 'value',
            })
          },
          svg: {
            filename: `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`
          },
          png: {
            filename: `${title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}`
          }
        }
      },
      zoom: { enabled: false },
      // Custom event handler to override CSV export with full data
      events: fullDataForExport ? {
        beforeExport: function (chartContext, config) {
          // Store original data
          chartContext._originalSeries = JSON.parse(JSON.stringify(chartContext.w.config.series));
          chartContext._originalCategories = JSON.parse(JSON.stringify(chartContext.w.config.xaxis.categories));

          // Replace with full data
          chartContext.w.config.series = fullDataForExport.series;
          chartContext.w.config.xaxis.categories = fullDataForExport.categories;
        },
        afterExport: function (chartContext, config) {
          // Restore original paginated data
          if (chartContext._originalSeries) {
            chartContext.w.config.series = chartContext._originalSeries;
            chartContext.w.config.xaxis.categories = chartContext._originalCategories;
          }
        }
      } : {}
    },
    colors: ['#5e72e4', '#11cdef', '#fb6340', '#f5365c', '#2dce89'],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    grid: {
      borderColor: '#f3f4f6',
      strokeDashArray: 4,
      padding: {
        top: 0,
        right: 20,
        bottom: 0,
        left: 20
      }
    },
    xaxis: {
      categories: xaxisCategories,
      labels: {
        style: { colors: '#8898aa', fontSize: '12px' }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      title: {
        text: yaxisTitle,
        style: { color: '#8898aa', fontSize: '12px', fontWeight: 500 }
      },
      labels: {
        style: { colors: '#8898aa', fontSize: '12px' }
      },
      tickAmount: Math.min(10, Math.ceil(maxVal / 10)),
      max: Math.ceil(maxVal / 10) * 10,
    },
    title: {
      text: title,
      align: 'left',
      style: { fontSize: '16px', fontWeight: 600, color: '#32325d' }
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: function (val) {
          return val;
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '45%',
        dataLabels: {
          position: 'top',
        },
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      offsetY: -20,
      fontFamily: "'Inter', sans-serif",
      markers: { radius: 12 },
    }
  });

  const paymentChartOptions = getCommonOptions(
    `Online vs Store Payment (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.payments[timeFrame]).paginatedCategories,
    'Count',
    getMaxValue(chartData.payments[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.payments[timeFrame]).allCategories,
      series: [
        {
          name: 'Online Payment',
          data: pagination(chartData.payments[timeFrame]).allCategories.map(
            key => chartData.payments[timeFrame][key].Online
          ),
        },
        {
          name: 'Store Payment',
          data: pagination(chartData.payments[timeFrame]).allCategories.map(
            key => chartData.payments[timeFrame][key].Store
          ),
        },
      ]
    }
  );

  const paymentChartSeries = [
    {
      name: 'Online Payment',
      data: pagination(chartData.payments[timeFrame]).paginatedCategories.map(
        key => chartData.payments[timeFrame][key].Online
      ),
    },
    {
      name: 'Store Payment',
      data: pagination(chartData.payments[timeFrame]).paginatedCategories.map(
        key => chartData.payments[timeFrame][key].Store
      ),
    },
  ];

  const orderChartOptions = getCommonOptions(
    `Web vs Mobile vs Instore Orders (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.orders[timeFrame]).paginatedCategories,
    'Order',
    getMaxValue(chartData.orders[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.orders[timeFrame]).allCategories,
      series: [
        {
          name: 'Web Orders',
          data: pagination(chartData.orders[timeFrame]).allCategories.map(key => chartData.orders[timeFrame][key].web || 0),
        },
        {
          name: 'Mobile Orders',
          data: pagination(chartData.orders[timeFrame]).allCategories.map(key => chartData.orders[timeFrame][key].mobile || 0),
        },
        {
          name: 'Instore Orders',
          data: pagination(chartData.orders[timeFrame]).allCategories.map(key => chartData.orders[timeFrame][key].instore || 0),
        },
      ]
    }
  );

  const orderChartSeries = [
    {
      name: 'Web Orders',
      data: pagination(chartData.orders[timeFrame]).paginatedCategories.map(key => chartData.orders[timeFrame][key].web || 0),
    },
    {
      name: 'Mobile Orders',
      data: pagination(chartData.orders[timeFrame]).paginatedCategories.map(key => chartData.orders[timeFrame][key].mobile || 0),
    },
    {
      name: 'Instore Orders',
      data: pagination(chartData.orders[timeFrame]).paginatedCategories.map(key => chartData.orders[timeFrame][key].instore || 0),
    },
  ]

  const userChartOptions = getCommonOptions(
    `New Customers (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.users[timeFrame]).paginatedCategories,
    'New Customers',
    getMaxValue(chartData.users[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.users[timeFrame]).allCategories,
      series: [
        {
          name: 'Web Customers',
          data: pagination(chartData.users[timeFrame]).allCategories.map(key => chartData.users[timeFrame][key]?.web || 0),
        },
        {
          name: 'Mobile Customers',
          data: pagination(chartData.users[timeFrame]).allCategories.map(key => chartData.users[timeFrame][key]?.mobile || 0),
        },
      ]
    }
  );
  // Specific override for user chart label rotation if needed, but common option should suffice or be adjusted
  userChartOptions.xaxis.labels = {
    ...userChartOptions.xaxis.labels,
    rotate: -45,
    rotateAlways: false,
    hideOverlappingLabels: true,
    trim: true,
  };

  const userChartSeries = [
    {
      name: 'Web Customers',
      data: pagination(chartData.users[timeFrame]).paginatedCategories.map(key => chartData.users[timeFrame][key]?.web || 0),
    },
    {
      name: 'Mobile Customers',
      data: pagination(chartData.users[timeFrame]).paginatedCategories.map(key => chartData.users[timeFrame][key]?.mobile || 0),
    },

  ]

  const salesChartOptions = getCommonOptions(
    `Sales (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.sales[timeFrame]).paginatedCategories,
    'Sales Amount',
    getMaxValue(chartData.sales[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.sales[timeFrame]).allCategories,
      series: [
        {
          name: 'Sales',
          data: pagination(chartData.sales[timeFrame]).allCategories.map(
            category => Math.round(chartData.sales[timeFrame][category])
          ),
        },
      ]
    }
  );
  salesChartOptions.yaxis.labels.formatter = (val) => Math.round(val);
  salesChartOptions.tooltip.y.formatter = (val) => Math.round(val);

  const salesChartSeries = [
    {
      name: 'Sales',
      data: pagination(chartData.sales[timeFrame]).paginatedCategories.map(
        category => Math.round(chartData.sales[timeFrame][category])
      ),
    },
  ];

  const tableBookingChartOptions = getCommonOptions(
    `Table Bookings (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.tableBookings[timeFrame]).paginatedCategories,
    'Table Booking',
    getMaxValue(chartData.tableBookings[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.tableBookings[timeFrame]).allCategories,
      series: [
        {
          name: 'Bookings',
          data: pagination(chartData.tableBookings[timeFrame]).allCategories.map(key => chartData.tableBookings[timeFrame][key]?.bookings || 0),
        },
        {
          name: 'Persons',
          data: pagination(chartData.tableBookings[timeFrame]).allCategories.map(key => chartData.tableBookings[timeFrame][key]?.persons || 0),
        },
      ]
    }
  );

  const tableBookingChartSeries = [
    {
      name: 'Bookings',
      data: pagination(chartData.tableBookings[timeFrame]).paginatedCategories.map(key => chartData.tableBookings[timeFrame][key]?.bookings || 0),
    },
    {
      name: 'Persons',
      data: pagination(chartData.tableBookings[timeFrame]).paginatedCategories.map(key => chartData.tableBookings[timeFrame][key]?.persons || 0),
    },
  ]

  const qrOrdersChartOptions = getCommonOptions(
    `QR Orders & Sales (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.qrOrders[timeFrame]).paginatedCategories,
    'Orders / Sales',
    getMaxValue(chartData.qrOrders[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.qrOrders[timeFrame]).allCategories,
      series: [
        {
          name: 'Orders',
          data: pagination(chartData.qrOrders[timeFrame]).allCategories.map(key => chartData.qrOrders[timeFrame][key]?.orders || 0),
        },
        {
          name: 'Sales',
          data: pagination(chartData.qrOrders[timeFrame]).allCategories.map(key => Math.round(chartData.qrOrders[timeFrame][key]?.sales || 0)),
        },
      ]
    }
  );

  const qrOrdersChartSeries = [
    {
      name: 'Orders',
      data: pagination(chartData.qrOrders[timeFrame]).paginatedCategories.map(key => chartData.qrOrders[timeFrame][key]?.orders || 0),
    },
    {
      name: 'Sales',
      data: pagination(chartData.qrOrders[timeFrame]).paginatedCategories.map(key => Math.round(chartData.qrOrders[timeFrame][key]?.sales || 0)),
    },
  ]

  // Determine which lead statuses to show based on filter and toggle
  const crmStatusesToShow = (selectedLeadStatus && !showAllStatuses)
    ? [selectedLeadStatus] // Show only the selected status when filtered
    : ['Contact', 'Lead', 'Prospect', 'Customer']; // Show all statuses

  // Color mapping for each status
  const statusColors = {
    'Contact': '#233dd2',
    'Lead': '#ff9800',
    'Prospect': '#e91e63',
    'Customer': '#4caf50'
  };

  const crmChartOptions = getCommonOptions(
    (selectedLeadStatus && !showAllStatuses)
      ? `${selectedLeadStatus} Trend (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`
      : `Lead Status Conversion Funnel (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.crmData[timeFrame]).paginatedCategories,
    'Count',
    getMaxValue(chartData.crmData[timeFrame]),
    // Full data for CSV export
    {
      categories: pagination(chartData.crmData[timeFrame]).allCategories,
      series: crmStatusesToShow.map(status => ({
        name: status === 'Lead' ? 'Leads' : status === 'Contact' ? 'Contacts' : status === 'Prospect' ? 'Prospects' : 'Customers',
        data: pagination(chartData.crmData[timeFrame]).allCategories.map(key => chartData.crmData[timeFrame][key]?.[status] || 0),
      }))
    }
  );
  // Set colors based on which statuses are shown
  crmChartOptions.colors = crmStatusesToShow.map(status => statusColors[status]);

  const crmChartSeries = crmStatusesToShow.map(status => ({
    name: status === 'Lead' ? 'Leads' : status === 'Contact' ? 'Contacts' : status === 'Prospect' ? 'Prospects' : 'Customers',
    data: pagination(chartData.crmData[timeFrame]).paginatedCategories.map(key => chartData.crmData[timeFrame][key]?.[status] || 0),
  }))

  const appointmentChartOptions = getCommonOptions(
    `Appointment Bookings (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.appointments[timeFrame]).paginatedCategories,
    'Bookings',
    getMaxValue(chartData.appointments[timeFrame]),
    {
      categories: pagination(chartData.appointments[timeFrame]).allCategories,
      series: [
        {
          name: 'Bookings',
          data: pagination(chartData.appointments[timeFrame]).allCategories.map(
            key => chartData.appointments[timeFrame][key] || 0
          ),
        },
      ]
    }
  );

  const appointmentChartSeries = [
    {
      name: 'Bookings',
      data: pagination(chartData.appointments[timeFrame]).paginatedCategories.map(
        key => chartData.appointments[timeFrame][key] || 0
      ),
    },
  ];

  const appointmentPaymentsChartOptions = getCommonOptions(
    `Appointment Payments (${timeFrame.charAt(0).toUpperCase() + timeFrame.slice(1)})`,
    pagination(chartData.appointmentPayments[timeFrame]).paginatedCategories,
    'Count',
    getMaxValue(chartData.appointmentPayments[timeFrame]),
    {
      categories: pagination(chartData.appointmentPayments[timeFrame]).allCategories,
      series: [
        {
          name: 'Successful',
          data: pagination(chartData.appointmentPayments[timeFrame]).allCategories.map(
            key => chartData.appointmentPayments[timeFrame][key].Successful || 0
          ),
        },
        {
          name: 'Unsuccessful',
          data: pagination(chartData.appointmentPayments[timeFrame]).allCategories.map(
            key => chartData.appointmentPayments[timeFrame][key].Unsuccessful || 0
          ),
        },
      ]
    }
  );

  const appointmentPaymentsChartSeries = [
    {
      name: 'Successful',
      data: pagination(chartData.appointmentPayments[timeFrame]).paginatedCategories.map(
        key => chartData.appointmentPayments[timeFrame][key].Successful || 0
      ),
    },
    {
      name: 'Unsuccessful',
      data: pagination(chartData.appointmentPayments[timeFrame]).paginatedCategories.map(
        key => chartData.appointmentPayments[timeFrame][key].Unsuccessful || 0
      ),
    },
  ];

  const emailUsageChartOptions = getCommonOptions(
    'Monthly Email Usage',
    pagination(chartData.emailUsage.monthly).paginatedCategories,
    'Usage Count',
    getMaxValue(chartData.emailUsage.monthly),
    {
      categories: pagination(chartData.emailUsage.monthly).allCategories,
      series: [
        {
          name: 'Emails Sent',
          data: pagination(chartData.emailUsage.monthly).allCategories.map(
            key => chartData.emailUsage.monthly[key] || 0
          ),
        },
      ]
    }
  );

  const emailUsageChartSeries = [
    {
      name: 'Emails Sent',
      data: pagination(chartData.emailUsage.monthly).paginatedCategories.map(
        key => chartData.emailUsage.monthly[key] || 0
      ),
    },
  ];

  const smsUsageChartOptions = getCommonOptions(
    'Monthly SMS Usage',
    pagination(chartData.smsUsage.monthly).paginatedCategories,
    'Usage Count',
    getMaxValue(chartData.smsUsage.monthly),
    {
      categories: pagination(chartData.smsUsage.monthly).allCategories,
      series: [
        {
          name: 'SMS Sent',
          data: pagination(chartData.smsUsage.monthly).allCategories.map(
            key => chartData.smsUsage.monthly[key] || 0
          ),
        },
      ]
    }
  );

  const smsUsageChartSeries = [
    {
      name: 'SMS Sent',
      data: pagination(chartData.smsUsage.monthly).paginatedCategories.map(
        key => chartData.smsUsage.monthly[key] || 0
      ),
    },
  ];

  const storageChartOptions = {
    chart: {
      type: 'donut',
      fontFamily: "'Inter', sans-serif",
    },
    labels: storageUsage?.folders?.map(f => f.prefix) || [],
    colors: ['#5e72e4', '#fb6340', '#2dce89', '#f5365c'],
    title: {
      text: 'S3 Storage Distribution',
      align: 'left',
      style: { fontSize: '16px', fontWeight: 600, color: '#32325d' }
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              formatter: (val) => {
                const folder = storageUsage?.folders?.find(f => f.totalSizeBytes === parseInt(val));
                return folder ? folder.totalSizeHuman : val;
              }
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => storageUsage?.summary?.totalSizeHuman || '0 B'
            }
          }
        }
      }
    },
    tooltip: {
      y: {
        formatter: (val, { seriesIndex }) => {
          const folder = storageUsage?.folders?.[seriesIndex];
          return folder ? `${folder.totalSizeHuman} (${folder.objectCount} objects)` : `${val} bytes`;
        }
      }
    },
    legend: {
      position: 'bottom'
    }
  };

  const storageChartSeries = storageUsage?.folders?.map(f => f.totalSizeBytes) || [];

  const renderChart = () => {
    switch (chartType) {
      case 'orders':
        return <Chart options={orderChartOptions} series={orderChartSeries} type="bar" height={350} />
      case 'payments':
        return <Chart options={paymentChartOptions} series={paymentChartSeries} type="bar" height={350} />
      case 'customers':
        return <Chart options={userChartOptions} series={userChartSeries} type="bar" height={350} />
      case 'sales':
        return <Chart options={salesChartOptions} series={salesChartSeries} type="bar" height={350} />
      case 'tableBookings':
        return <Chart options={tableBookingChartOptions} series={tableBookingChartSeries} type="bar" height={350} />
      case 'qrOrders':
        return <Chart options={qrOrdersChartOptions} series={qrOrdersChartSeries} type="bar" height={350} />
      case 'crm':
        return <Chart options={crmChartOptions} series={crmChartSeries} type="bar" height={350} />
      case 'appointments':
        return <Chart options={appointmentChartOptions} series={appointmentChartSeries} type="bar" height={350} />
      case 'appointmentPayments':
        return <Chart options={appointmentPaymentsChartSeries} series={appointmentPaymentsChartSeries} type="bar" height={350} />
      case 'storage':
        return <Chart options={storageChartOptions} series={storageChartSeries} type="donut" height={350} />
      case 'emailUsage':
        return <Chart options={emailUsageChartOptions} series={emailUsageChartSeries} type="bar" height={350} />
      case 'smsUsage':
        return <Chart options={smsUsageChartOptions} series={smsUsageChartSeries} type="bar" height={350} />
      default:
        return null
    }
  }

  const getTotalPages = () => {
    if (!chartData) return 1
    let typeOfData
    if (chartType === 'customers') typeOfData = chartData.users[timeFrame]
    else if (chartType === 'orders') typeOfData = chartData.orders[timeFrame]
    else if (chartType === 'payments') typeOfData = chartData.payments[timeFrame]
    else if (chartType === 'tableBookings') typeOfData = chartData.tableBookings[timeFrame]
    else if (chartType === 'sales') typeOfData = chartData.sales[timeFrame]
    else if (chartType === 'qrOrders') typeOfData = chartData.qrOrders[timeFrame]
    else if (chartType === 'crm') typeOfData = chartData.crmData[timeFrame]
    else if (chartType === 'appointments') typeOfData = chartData.appointments[timeFrame]
    else if (chartType === 'appointmentPayments') typeOfData = chartData.appointmentPayments[timeFrame]
    else if (chartType === 'emailUsage') typeOfData = chartData.emailUsage.monthly
    else if (chartType === 'smsUsage') typeOfData = chartData.smsUsage.monthly
    else if (chartType === 'storage') return 1
    if (!typeOfData) return 1
    const totalCategories = Object.keys(typeOfData).length
    return Math.max(1, Math.ceil(totalCategories / dataPerPage));
  }

  const totalPages = getTotalPages();

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff', borderRadius: '16px', border: '1px solid #f1f5f9', p: 3 }}>
      {chartType !== 'storage' && chartType !== 'emailUsage' && chartType !== 'smsUsage' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {/* Time Frame Toggle */}
          <ToggleButtonGroup
            value={timeFrame}
            exclusive
            onChange={handleTimeFrameChange}
            aria-label="time frame"
            size="small"
            sx={{
              bgcolor: '#f8fafc',
              p: 0.5,
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: '8px !important',
                px: 3,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                color: '#64748b',
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  bgcolor: '#fff',
                  color: '#3b82f6',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                  '&:hover': {
                    bgcolor: '#fff',
                  }
                },
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#1e293b'
                }
              }
            }}
          >
            <ToggleButton value="monthly">
              Monthly
            </ToggleButton>
            <ToggleButton value="weekly">
              Weekly
            </ToggleButton>
          </ToggleButtonGroup>

          {/* CRM Status Filter Toggle - Only show for CRM charts when a status was selected */}
          {chartType === 'crm' && selectedLeadStatus && (
            <ToggleButtonGroup
              value={showAllStatuses ? 'all' : 'single'}
              exclusive
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setShowAllStatuses(newValue === 'all')
                }
              }}
              aria-label="status filter"
              size="small"
              sx={{
                bgcolor: '#f8fafc',
                p: 0.5,
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px !important',
                  px: 3,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#64748b',
                  fontSize: '0.875rem',
                  '&.Mui-selected': {
                    bgcolor: '#fff',
                    color: '#3b82f6',
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                    '&:hover': {
                      bgcolor: '#fff',
                    }
                  },
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#1e293b'
                  }
                }
              }}
            >
              <ToggleButton value="single">
                {selectedLeadStatus} Only
              </ToggleButton>
              <ToggleButton value="all">
                All Statuses
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      )}

      <Box sx={{ minHeight: 400 }}>
        {renderChart()}
      </Box>

      {chartType !== 'storage' && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '8px',
                fontWeight: 600
              }
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default DashboardCharts